/**
 * API Endpoints Configuration
 * Centralized endpoint definitions
 * Auth (login, OTP) uses staging base; override via env for dev/prod.
 *
 * @author Varun Pandey
 */

const API_BASE =
  import.meta.env.VITE_API_PATH || "https://artist-api.starclinch.com/api/";

// Auth APIs (login, OTP request/verify) - same base for all
const AUTH_API_BASE =
  import.meta.env.VITE_AUTH_API_BASE || "https://stagi.starclinch.com/api/v2/";

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: `${AUTH_API_BASE}auth/login/`,
    OTP_REQUEST: `${AUTH_API_BASE}auth/login/otp/request/`,
    OTP_VERIFY: `${AUTH_API_BASE}auth/login/otp/verify/`,
    CHECK_EXISTENCE: `${AUTH_API_BASE}auth/check-existence/`,
    SIGNUP_OTP_SEND: `${AUTH_API_BASE}auth/signup/otp/send/`,
    SIGNUP_OTP_VERIFY: `${AUTH_API_BASE}auth/signup/otp/verify/`,
    SIGNUP: `${AUTH_API_BASE}auth/signup/`,
    SIGNUP_SSO: `${AUTH_API_BASE}auth/signup/sso/`,
    ONBOARDING: `${AUTH_API_BASE}auth/onboarding/`,
    ACTIVE_ARTIST: `${AUTH_API_BASE}auth/active-artist/`,
    ARTIST_DETAILS: `${AUTH_API_BASE}artist/details/`,
    AGENCY_DETAIL: `${AUTH_API_BASE}agency/detail/`,
    AGENCY_SUB_USERS: `${AUTH_API_BASE}agency/sub-users/`,
    AGENCY_SUB_USER: (id: number) => `${AUTH_API_BASE}agency/sub-users/${id}/`,
    AGENCY_ARTISTS_ONBOARDING: `${AUTH_API_BASE}agency/artists/onboarding/`,
    DELETE_ARTIST_FROM_AGENCY: `${AUTH_API_BASE}agency/artists/remove/`,
    PERSONAL_DETAILS: `${AUTH_API_BASE}artist/personal-details/`,
    PERFORMANCE_DETAILS: `${AUTH_API_BASE}artist/performance-details/`,
    PERFORMING_MEMBER_DELETE: (id: number) =>
      `${AUTH_API_BASE}artist/performance-details/performing-members/${id}/`,
    ARTIST_AWARDS: `${AUTH_API_BASE}artist/awards/`,
    ARTIST_AWARD_DELETE: (id: number) => `${AUTH_API_BASE}artist/awards/${id}/`,
    ARTIST_PAST_PERFORMANCES: `${AUTH_API_BASE}artist/past-performances/`,
    ARTIST_PAST_PERFORMANCE_DELETE: (id: number) =>
      `${AUTH_API_BASE}artist/past-performances/${id}/`,
    ARTIST_USP: `${AUTH_API_BASE}artist/usp/`,
    ARTIST_STORY: `${AUTH_API_BASE}artist/story/`,
    ARTIST_CREDIBILITY: `${AUTH_API_BASE}artist/credibility/`,
    PROFILE_PIC: `${AUTH_API_BASE}artist/profile-pic/`,
    REGISTER: `${API_BASE}auth/register`,
    LOGOUT: `${AUTH_API_BASE}auth/logout/`,
    VERIFY_SESSION: `${AUTH_API_BASE}auth/verify-session/`,
    REFRESH: `${API_BASE}auth/refresh`,
    FORGOT_PASSWORD: `${API_BASE}auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE}auth/reset-password`,
    VERIFY_EMAIL: `${API_BASE}auth/verify-email`,
    CATEGORIES: `${AUTH_API_BASE}categories/`,
    SUBCATEGORIES: (slug: string) =>
      `${AUTH_API_BASE}categories/${slug}/subcategories/`,
    SIMILAR_PROFILES: `${AUTH_API_BASE}similar-profiles/`,
    CLAIM_PROFILE: `${AUTH_API_BASE}claim-profile/`,
    PASSWORD_RESET_REQUEST_OTP: `${AUTH_API_BASE}auth/password/reset/send-otp/`,
    PASSWORD_RESET_VERIFY_OTP: `${AUTH_API_BASE}auth/password/reset/verify-otp/`,
    PASSWORD_RESET: `${AUTH_API_BASE}auth/password/reset/`,
    PROFILE_VERIFY_SEND_OTP: `${AUTH_API_BASE}auth/profile/verify/send-otp/`,
    PROFILE_VERIFY_OTP_VERIFY: `${AUTH_API_BASE}auth/profile/verify/otp/`,
    JOBS: `${AUTH_API_BASE}jobs/`,
    JOBS_DETAIL: (id: number | string) => `${AUTH_API_BASE}jobs/${id}/`,
    JOBS_EXPRESS_BOOKING_DETAIL: (dealId: number | string) =>
      `${AUTH_API_BASE}jobs/express-booking/${dealId}/`,
    JOBS_EXPRESS_BOOKING_VERIFY_OTP: (dealId: number | string) =>
      `${AUTH_API_BASE}jobs/express-booking/${dealId}/verify-otp/`,
    JOBS_EXPRESS_BOOKING_PERFORMANCE_START: `${AUTH_API_BASE}jobs/express-booking/performance-start/`,
    JOBS_LIKED: `${AUTH_API_BASE}jobs/like/`,
    JOBS_BIDS: `${AUTH_API_BASE}jobs/bids/`,
    JOBS_BID_SUBMIT: (id: number | string) =>
      `${AUTH_API_BASE}jobs/bids/${id}/`,
    ARTIST_GALLERY_IMAGES: `${AUTH_API_BASE}artist/gallery/images/`,
    ARTIST_GALLERY_IMAGE_DELETE: (id: number | string) =>
      `${AUTH_API_BASE}artist/gallery/images/${id}/`,
    ARTIST_GALLERY_VIDEOS: `${AUTH_API_BASE}artist/gallery/videos/`,
    ARTIST_GALLERY_VIDEO_DELETE: (id: number | string) =>
      `${AUTH_API_BASE}artist/gallery/videos/${id}/`,
    ARTIST_EVENT_PRICING: `${AUTH_API_BASE}artist/event-pricing/`,
    ARTIST_EVENT_PRICING_DELETE: (slug: string) =>
      `${AUTH_API_BASE}artist/event-pricing/${slug}/`,
    ARTIST_SPECIAL_EVENT_PRICING: (slug: string) =>
      `${AUTH_API_BASE}artist/special-event-pricing/${slug}/`,
    ARTIST_TECH_RIDER_TBL: `${AUTH_API_BASE}artist/tech-rider-tbl/`,
    ARTIST_BILLING: `${AUTH_API_BASE}artist/billing/`,
    ARTIST_BILLING_GST_DETAILS: `${AUTH_API_BASE}artist/billing/gst-details/`,
    ARTIST_BILLING_BANK_ACCOUNTS: `${AUTH_API_BASE}artist/billing/bank-accounts/`,
    ARTIST_BILLING_BANK_ACCOUNT: (id: number) =>
      `${AUTH_API_BASE}artist/billing/bank-accounts/${id}/`,
    // Contact (primary: verify OTP, modification request; secondary: update)
    ARTIST_CONTACT_OTP_EMAIL_SEND: `${AUTH_API_BASE}artist/contact/otp/email/send/`,
    ARTIST_CONTACT_OTP_EMAIL_VERIFY: `${AUTH_API_BASE}artist/contact/otp/email/verify/`,
    ARTIST_CONTACT_OTP_PHONE_SEND: `${AUTH_API_BASE}artist/contact/otp/phone/send/`,
    ARTIST_CONTACT_OTP_PHONE_VERIFY: `${AUTH_API_BASE}artist/contact/otp/phone/verify/`,
    ARTIST_CONTACT_SECONDARY: `${AUTH_API_BASE}artist/contact/secondary/`,
    ARTIST_CONTACT_DETAILS: `${AUTH_API_BASE}artist/contact-details/`,
    ARTIST_CONTACT_US: `${AUTH_API_BASE}artist/contact-us/`,
    ARTIST_BOOKING_FORMS: `${AUTH_API_BASE}artist/booking-forms/`,
    ARTIST_BOOKING_FORMS_BOOKED: `${AUTH_API_BASE}artist/booking-forms/booked/`,
    ARTIST_BOOKING_FORM_SUBMIT: (id: number | string) =>
      `${AUTH_API_BASE}artist/booking-forms/${id}/submit/`,
    // Elite Members list (public)
    ELITE_MEMBERS: `${AUTH_API_BASE}artists/elite-members/`,
    // Account deletion (iOS App Store)
    ACCOUNT_DELETION_STATUS: `${AUTH_API_BASE}artist/account-deletion/status/`,
    ACCOUNT_DELETION_REASONS: `${AUTH_API_BASE}artist/account-deletion/reasons/`,
    ACCOUNT_DELETION_SUBMIT: `${AUTH_API_BASE}artist/account-deletion/`,
    ACCOUNT_DELETION_REACTIVATE: `${AUTH_API_BASE}artist/account-deletion/reactivate/`,
  },

  // Express Booking (artist feature; lives on the auth v2 base)
  EXPRESS_BOOKING: {
    BASE: `${AUTH_API_BASE}artist/profile/express-booking/`,
    LOCATIONS: `${AUTH_API_BASE}artist/profile/express-booking/locations/`,
    LOCATION_DELETE: (id: number) =>
      `${AUTH_API_BASE}artist/profile/express-booking/locations/${id}/`,
  },

  // User
  USER: {
    PROFILE: `${API_BASE}user/profile`,
    UPDATE_PROFILE: `${API_BASE}user/profile`,
    DELETE_ACCOUNT: `${API_BASE}user/account`,
  },

  // Instagram
  INSTAGRAM: {
    CONNECT: `${API_BASE}instagram/connect`,
    CALLBACK: `${API_BASE}instagram/callback`,
    DISCONNECT: `${API_BASE}instagram/disconnect`,
    MEDIA: `${API_BASE}instagram/media`,
    STATS: `${API_BASE}instagram/stats`,
    SYNC: `${API_BASE}instagram/sync`,
  },

  // Social media (v2 artist endpoints)
  SOCIAL: {
    ARTIST_INSTAGRAM_ENGAGEMENT: `${AUTH_API_BASE}artist/instagram/engagement/`,
    ARTIST_INSTAGRAM_CONNECT: `${AUTH_API_BASE}artist/instagram/connect/`,
    ARTIST_YOUTUBE_ENGAGEMENT: `${AUTH_API_BASE}artist/youtube/engagement/`,
    ARTIST_YOUTUBE_CONNECT: `${AUTH_API_BASE}artist/youtube/connect/`,
  },

  // Profile
  PROFILE: {
    GET: (userId: string) => `${API_BASE}profile/${userId}`,
    UPDATE: `${API_BASE}profile`,
    COMPLETION: `${API_BASE}profile/completion`,
    GALLERY: `${API_BASE}profile/gallery`,
    UPLOAD_MEDIA: `${API_BASE}profile/gallery/upload`,
    DELETE_MEDIA: (mediaId: string) => `${API_BASE}profile/gallery/${mediaId}`,
  },

  // Bookings
  BOOKINGS: {
    LIST: `${API_BASE}bookings`,
    GET: (bookingId: string) => `${API_BASE}bookings/${bookingId}`,
    UPDATE_STATUS: (bookingId: string) =>
      `${API_BASE}bookings/${bookingId}/status`,
    ACCEPT: (bookingId: string) => `${API_BASE}bookings/${bookingId}/accept`,
    REJECT: (bookingId: string) => `${API_BASE}bookings/${bookingId}/reject`,
  },

  // Wallet & Payments
  WALLET: {
    BALANCE: `${AUTH_API_BASE}wallet/`,
    HISTORY: `${AUTH_API_BASE}wallet/history/`,
    CREATE_ORDER: `${AUTH_API_BASE}wallet/create/`,
    VERIFY_PAYMENT: `${AUTH_API_BASE}wallet/verify-payment/`,
  },

  // Plans & Membership
  PLANS: {
    BUY: `${AUTH_API_BASE}plans/buy/`,
    ELITE_ELIGIBILITY: `${AUTH_API_BASE}plans/elite-eligibility/`,
    /** iOS membership CTA: generate one-time website login link by phone. */
    ONE_TIME_LOGIN_LINK_BY_PHONE_FOR_MEMBERSHIP:
      `${AUTH_API_BASE}auth/one-time-login/workflow-short-link/`,
    /** iOS: validate plan before StoreKit purchase (optional). */
    IAP_PREPARE: `${AUTH_API_BASE}plans/iap/prepare/`,
    /** iOS: sync RevenueCat purchase to Django after successful StoreKit transaction. */
    IAP_SYNC: `${AUTH_API_BASE}plans/iap/sync/`,
    /** iOS: restore purchases — client calls after Purchases.restorePurchases(). */
    IAP_RESTORE: `${AUTH_API_BASE}plans/iap/restore/`,
  },

  // Subscription
  SUBSCRIPTION: {
    PLANS: `${API_BASE}subscription/plans`,
    CURRENT: `${API_BASE}subscription/current`,
    SUBSCRIBE: `${API_BASE}subscription/subscribe`,
    CANCEL: `${API_BASE}subscription/cancel`,
    UPGRADE: `${API_BASE}subscription/upgrade`,
  },

  // Notifications
  NOTIFICATIONS: {
    LIST: `${API_BASE}notifications`,
    READ: (notificationId: string) =>
      `${API_BASE}notifications/${notificationId}/read`,
    READ_ALL: `${API_BASE}notifications/read-all`,
    SETTINGS: `${API_BASE}notifications/settings`,
    /** OneSignal device subscription upsert (POST) / deactivate (DELETE). */
    PUSH_SUBSCRIPTIONS_REGISTER: `${AUTH_API_BASE}notifications/push-subscriptions/register/`,
    /** List active push subscriptions for the authenticated user. */
    PUSH_SUBSCRIPTIONS: `${AUTH_API_BASE}notifications/push-subscriptions/`,
  },

  CHAT: {
    THREADS: `${AUTH_API_BASE}chat/threads/`,
    THREAD: (id: number | string) => `${AUTH_API_BASE}chat/threads/${id}/`,
    THREAD_MESSAGES: (id: number | string) =>
      `${AUTH_API_BASE}chat/threads/${id}/messages/`,
  },

  // Analytics
  ANALYTICS: {
    DASHBOARD: `${AUTH_API_BASE}analytics/dashboard/`,
    STATS: `${AUTH_API_BASE}analytics/stats/`,
    STATS_BASIC: `${AUTH_API_BASE}stats/basic/`,
    CATEGORY_PERFORMANCE: `${AUTH_API_BASE}stats/category-performance/`,
    REGIONAL_RANK: `${AUTH_API_BASE}stats/regional-rank/`,
  },
} as const;